import React, { useState, useEffect } from 'react';
import { RefreshCw, Sparkles, X } from 'lucide-react';
import { Button } from './ui/button';
import { getCurrentRelease } from '../data/releaseNotes';

// APP_VERSION is derived from the latest entry in /data/releaseNotes.js so the
// two sources of truth can never drift. To ship a new version, add a new entry
// to the top of RELEASES in releaseNotes.js — APP_VERSION updates automatically.
export const APP_VERSION = getCurrentRelease().version || '5.9.0';

/**
 * Version Checker Component
 * Checks for app updates and prompts users to refresh, especially important for PWAs
 */
const VersionChecker = () => {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const [newVersion, setNewVersion] = useState(null);

  useEffect(() => {
    // Check stored version vs current version
    const storedVersion = localStorage.getItem('writeright_app_version');
    
    if (storedVersion && storedVersion !== APP_VERSION) {
      // New version detected!
      setNewVersion(APP_VERSION);
      setShowUpdateBanner(true);
    }
    
    // Store current version
    localStorage.setItem('writeright_app_version', APP_VERSION);
    
    // Also check for service worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          // Check for updates
          registration.update().catch(() => {});
          
          // Listen for new service worker
          registration.addEventListener('updatefound', () => {
            const newWorker = registration.installing;
            if (newWorker) {
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                  // New version available
                  setShowUpdateBanner(true);
                  setNewVersion(APP_VERSION);
                }
              });
            }
          });
        });
      });
    }
    
    // Periodic check (every 5 minutes) for PWAs that stay open
    const intervalId = setInterval(() => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(registrations => {
          registrations.forEach(registration => {
            registration.update().catch(() => {});
          });
        });
      }
    }, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const handleRefresh = () => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.unregister();
        });
      });
    }
    
    // Clear localStorage version to force fresh check
    localStorage.removeItem('writeright_app_version');
    
    // Hard refresh
    window.location.reload(true);
  };

  const handleDismiss = () => {
    setShowUpdateBanner(false);
    // Store that user dismissed this version's update
    localStorage.setItem('writeright_dismissed_version', APP_VERSION);
  };

  // Don't show if user already dismissed this version
  useEffect(() => {
    const dismissedVersion = localStorage.getItem('writeright_dismissed_version');
    if (dismissedVersion === APP_VERSION) {
      setShowUpdateBanner(false);
    }
  }, []);

  if (!showUpdateBanner) return null;

  return (
    // Mar 3, 2026 — Provider reported the banner was orange bg + white
    // text = ~2.9:1 contrast (fails WCAG AA 4.5:1 for normal text) and
    // hard to see. Switched to amber-400 bg + slate-900 text = ~11:1
    // contrast — much more legible while keeping the "attention" hue.
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-amber-400 text-slate-900 px-4 py-2 shadow-lg ring-2 ring-amber-600/60 border-b-2 border-amber-600">
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 shrink-0 text-slate-900" />
          <span className="text-sm font-bold text-slate-900">
            New version {newVersion || APP_VERSION} available! Refresh to get the latest features.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={handleRefresh}
            size="sm"
            className="bg-slate-900 text-amber-300 hover:bg-slate-800 gap-1.5 font-bold ring-2 ring-slate-900/40 shadow-md"
            data-testid="app-update-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 text-amber-300" />
            Refresh Now
          </Button>
          <button
            onClick={handleDismiss}
            className="p-1.5 hover:bg-slate-900/10 rounded text-slate-900"
            title="Dismiss"
            data-testid="app-update-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VersionChecker;
